import { useState } from 'react';

import { MenuItem, SelectChangeEvent } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import InputForm from 'shared/components/Input/InputForm';
import { APIErrorT } from 'shared/models/api';
import { SCHCTemplateRuleEntryValidFieldNames, SCHCTemplateStackT } from 'shared/models/schc-templates';
import SCHCTemplate from 'shared/models/SCHCTemplate';
import api from 'utils/api';
import { generateRandomString } from 'utils/string';

interface StackFieldDisplayedT extends SCHCTemplate.Form.CompressionItem {
  index: number;// Index allows to keep the reference of non displayed fields
}
interface SCHCTemplatesRuleFormCompressionStackProps {
  index: number;
  stacksAvailable: Array<SCHCTemplateStackT>;
}
function SCHCTemplatesRuleFormCompressionStack({
  index, stacksAvailable,
}: SCHCTemplatesRuleFormCompressionStackProps) {
  const {
    register,
    unregister,
    getValues,
    control,
    formState: {
      errors,
    },
  } = useFormContext();
  const { schcTemplateId } = useParams() as RouterParams;
  const isEdit = schcTemplateId !== undefined;

  const ruleValuesToEdit: SCHCTemplate.Form.RuleT = getValues().rules[index] || [];
  const defaultStack = isEdit ? ruleValuesToEdit.stack : '';

  /*
    Details:
    The stack fields contains fields that are :
    -> Displayed
    -> Not displayed
    ...on the visual form but both types need to be sent to the backend.
    In order to do that we conserve the INDEX of the fields.
  */

  /* === Loaded on UPDATE === */
  const initDefaultStackFields = (): Array<StackFieldDisplayedT> => {
    let tmpStackFields: any[] | undefined = [];
    if (isEdit) {
      tmpStackFields = ruleValuesToEdit.fields || [];
      // Add index
      tmpStackFields = tmpStackFields?.map(
        (item: SCHCTemplate.Form.CompressionItem, i: number) => ({
          ...item,
          index: i,
        }),
      );
      // Remove fields not displayed
      tmpStackFields = tmpStackFields?.filter(
        (item: SCHCTemplate.Form.CompressionItem) => item.value !== undefined,
      );
    }
    return tmpStackFields as StackFieldDisplayedT[];
  };
  const defaultStackFields = initDefaultStackFields();

  const [
    stackFields,
    setStackFields,
  ] = useState<StackFieldDisplayedT[]>(defaultStackFields as Array<StackFieldDisplayedT>);

  /* === Loaded on get NEW Stack Fields === */
  const getStackFieldsFromBackend = (stack: string) => {
    api.SCHCTemplates.getDefaultRules(stack)
      .then((resp: Response) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
        } else {
          resp.json()
            .then((data: SCHCTemplate.API.Compression.RuleT) => {
              if (data.flows.length > 0) {
                const { ruleEntries } = data.flows[0];
                const stackFieldsDisplayed: StackFieldDisplayedT[] = [];
                /*
                  Extract the ruleEntries that will be displayed
                  as input in the frontend
                */
                ruleEntries.forEach((
                  ruleEntry: SCHCTemplate.API.Compression.Flow.RuleEntriesT,
                  i: number,
                ) => {
                  const isToDisplay = SCHCTemplateRuleEntryValidFieldNames
                    .includes(ruleEntry.fieldName);
                  if (isToDisplay) {
                    stackFieldsDisplayed.push({
                      ...ruleEntry,
                      value: ruleEntry.targetValue[0],
                      index: i,
                    });
                  }
                  /*
                    Register in the react hook form each
                    field with his default value
                    received from the backend template
                  */
                  Object.keys(ruleEntry).forEach((key) => {
                    register(`rules.${index}.fields.${i}.${key}`, { value: (ruleEntry as any)[key] });
                  });
                });
                setStackFields(stackFieldsDisplayed);
              } else {
                console.error('The data collected from the backend is invalid, data.flows should not be empty');
              }
            });
        }
      });
  };

  const onSelectStack = (event: SelectChangeEvent<any>) => {
    /*
    Reset the registered rule fields
    in case onSelectStack has been called previously
    */
    unregister(`rules.${index}.fields`);
    /* Collect the rules default settings defined by the backend */
    const stack = event.target.value as string;
    getStackFieldsFromBackend(stack);
  };

  return (
    <>
      <InputForm.Select
        label="Stack"
        name={`rules.${index}.stack`}
        control={control}
        onChange={onSelectStack}
        fullWidth
        sx={{ mt: 1, mb: 1 }}
        hasError={Array.isArray(errors.rules) && !!errors.rules[index]?.stack}
        defaultValue={defaultStack}
      >
        {stacksAvailable.map((stack) => (
          <MenuItem key={stack.stack} value={stack.stack}>
            { stack.description }
          </MenuItem>
        ))}
      </InputForm.Select>
      <div className="fields-side-line">
        {
          stackFields.map((stackField) => (
            <InputForm.TextField
              fullWidth
              sx={{ mt: 1, mb: 1 }}
              key={generateRandomString()}
              label={stackField.displayName}
              defaultValue={stackField.value}
              register={register(`rules.${index}.fields.${stackField.index}.value`)}
            />
          ))
        }
      </div>
    </>
  );
}

export default SCHCTemplatesRuleFormCompressionStack;
